import { createElement } from "react";
import { createRoute, lazyRouteComponent } from "@tanstack/react-router";

import { requirePermission } from "../../../../shared/routing/requirePermission";
import {
  USER_MANAGEMENT_CREATE,
  USER_MANAGEMENT_UPDATE,
  USER_MANAGEMENT_VIEW,
} from "../../usersPermissions";
import {
  archivedOperationalUsersSearchSchema,
  operationalUsersSearchSchema,
} from "./searchSchemas";
import type { UsersRouteFactory } from "./usersRouteContext";

const UsersListScreen = lazyRouteComponent(
  () => import("../UsersListScreen"),
  "UsersListScreen",
);
const ArchivedUsersListScreen = lazyRouteComponent(
  () => import("../ArchivedUsersListScreen"),
  "ArchivedUsersListScreen",
);
const UserCreateScreen = lazyRouteComponent(
  () => import("../UserCreateScreen"),
  "UserCreateScreen",
);
const UserEditScreen = lazyRouteComponent(
  () => import("../UserEditScreen"),
  "UserEditScreen",
);
const UserDetailScreen = lazyRouteComponent(
  () => import("../UserDetailScreen"),
  "UserDetailScreen",
);

export const createOperationalUsersRoutes: UsersRouteFactory = ({
  context,
  operationalUsers,
  branches,
  departments,
  roles,
}) => {
  // basePath is a runtime string, so the template loses the literal type
  // TanStack Router needs to type params -- $id is guaranteed by the path
  // (same as createDriversRoutes).
  const detailRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${context.basePath}/$id`,
    beforeLoad: requirePermission([USER_MANAGEMENT_VIEW]),
    component: function UserDetailRouteScreen() {
      const { id } = detailRoute.useParams() as unknown as { id: string };
      return createElement(UserDetailScreen, {
        repository: operationalUsers,
        basePath: context.basePath,
        userId: id,
        permissionCatalog: context.permissionCatalog,
      });
    },
  });
  const editRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${context.basePath}/$id/edit`,
    beforeLoad: requirePermission([USER_MANAGEMENT_UPDATE]),
    component: function UserEditRouteScreen() {
      const { id } = editRoute.useParams() as unknown as { id: string };
      return createElement(UserEditScreen, {
        repository: operationalUsers,
        basePath: context.basePath,
        userId: id,
        branches,
        departments,
        roles,
        permissionCatalog: context.permissionCatalog,
      });
    },
  });

  return [
    createRoute({
      getParentRoute: context.getParentRoute,
      path: context.basePath,
      validateSearch: operationalUsersSearchSchema,
      beforeLoad: requirePermission([USER_MANAGEMENT_VIEW]),
      component: () =>
        createElement(UsersListScreen, {
          repository: operationalUsers,
          basePath: context.basePath,
        }),
    }),
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/archived`,
      validateSearch: archivedOperationalUsersSearchSchema,
      beforeLoad: requirePermission([USER_MANAGEMENT_VIEW]),
      component: () =>
        createElement(ArchivedUsersListScreen, {
          repository: operationalUsers,
          basePath: context.basePath,
        }),
    }),
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/create`,
      beforeLoad: requirePermission([USER_MANAGEMENT_CREATE]),
      component: () =>
        createElement(UserCreateScreen, {
          repository: operationalUsers,
          basePath: context.basePath,
          branches,
          departments,
          roles,
          permissionCatalog: context.permissionCatalog,
        }),
    }),
    editRoute,
    detailRoute,
  ];
};
